import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as R from "ramda";
import { Button, Divider, Icon, List, message, Select } from "antd";

import { createVideoByTimeline } from "../aliyun-service-api";
import { FloatToItsParentRight } from "@/components/fixed-to-its-parent";
import { SelectOneTemplate } from "@/containers/Video/tab-pages/video-template";
import { HoverEditableInput } from "@/containers/Video/widgets/hover-editable-input";
import { uploadContentToOssFile } from "@/containers/Video/widgets/upload-resource-util";

export const humanReadableTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const sec = seconds % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${sec.toFixed(2).padStart(5, "0")}`;
};

export const debounce = (fn, delay = 500) => {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const textIncludesEmoji = (text) => {
  // noinspection RegExpDuplicateCharacterInClass
  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2B50}\u{2B55}\u{2934}\u{2935}\u{2B05}\u{2B06}\u{2B07}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F170}-\u{1F171}\u{1F17E}-\u{1F17F}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}-\u{1F202}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F23A}\u{1F250}-\u{1F251}\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}-\u{1F202}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F23A}\u{1F250}-\u{1F251}\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}-\u{1F202}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F23A}\u{1F250}-\u{1F251}]/u;
  return emojiRegex.test(text);
};


export const uniqListByKey = (list, key) => {
  return R.uniqBy(R.prop(key), list);
};

export const sortByKeyAsc = (list, key) => {
  return R.sortBy(R.prop(key), list);
};

// noinspection JSUnusedLocalSymbols
const uniqAndSort = (list, key) => {
  return sortByKeyAsc(uniqListByKey(list, key), key);
};

/**
 * @param props {object} {
 *   detail: object, // 详情页的数据
 *   target: ref, // HTMLMediaElement ref
 *   asrList: array<{startTime: 3.22, endTime: 15.274, text: 'hello'}>,
 *
 *   1. 传入的参数就是一个简单的target, 是一个ref对象, 存储的是 HTMLMediaElement 的引用
 *   2. asrList 是一个数组, 数组中的每个元素都是一个对象, 对象中包含了三个属性, startTime, endTime, text
 *
 *   基本功能:
 *   1. 当 target 播放的时候, 会根据当前的时间, 高亮显示对应的 asrList 中的元素
 *   2. 点击 asrList 中的某一项, 会跳转到对应的时间点
 *   3. 字幕的显示格式为: [3.22 - 15.274] hello,且列表可能很长
 * }
 * @constructor
 */

/**
 * 将秒数转换为可读的时间格式
 *  4.32 => 00:04.32
 *  95.34 => 01:35.34
 *  609.11 => 10:09.11
 *  3648.12 => 01:00:48.12
 * @param seconds
 */
export const formatSecondsToHumanReadable = (seconds) => {
  const time = humanReadableTime(seconds);
  // [prev].[next], 前面的用正常字体, 后面的用灰色字体, 放在下面, 字号transform: scale(0.2), transformOrigin: "left bottom"
  const prev = time.split(".")[0];
  const next = time.split(".")[1];
  return (
    <span>
      {prev}
      <span style={{ fontSize: "0.5em" }}>
        .<span style={{ color: "#bfbfbf" }}>{next}</span>
      </span>
    </span>
  );
};

export const AsrLine = (props) => {
  const mediaId = R.pathOr("", ["detail", "item", "id"], props);
  const userId = R.pathOr("", ["userId"], props);
  const goldSlogan = R.pathOr([], ["goldSlogan"], props);
  const { target, asrList: originAsrList } = props;

  const asrRef = useRef(null);

  const [asrList, setAsrList] = useState(originAsrList);
  useEffect(() => {
    setAsrList(
      originAsrList.map((item, index) => ({
        ...item,
        value: index,
        label: item.text,
      }))
    );
  }, [originAsrList]);

  const originAsrIsSameWithAsr =useMemo(()=>{
    return R.equals(
      R.map(R.omit(["golden", 'value', 'label']))(originAsrList),
      R.map(R.omit(["golden", 'value', 'label']))(asrList)
    );
  }, [originAsrList, asrList]);

  const reloadAsr = R.pathOr(R.F, ["reloadAsr"], props);

  const [currentTime, setCurrentTime] = useState(0);
  const [selectedIndexList, setSelectedIndexList] = useState([]);

  const [searchKeyword, setSearchKeyword] = useState("");

  const debounceSetSearchKeyword = useMemo(
    () => debounce(setSearchKeyword, 300),
    [setSearchKeyword]
  );

  const [asrIsUploading, setAsrIsUploading] = useState(false);

  const [goldSloganIsExpanded, setGoldSloganIsExpanded] = useState(false);

  const url = R.pathOr("", ["detail", "item", "asr_oss_url"], props);

  const updateAsr = useCallback(
    (list) => {
      setAsrIsUploading(true);
      const listOmitGolden = R.map(R.omit(["golden"]), list);
      const emojiIndex = list.findIndex((item) => textIncludesEmoji(item.text));

      if (emojiIndex !== -1) {
        message.error("字幕中包含表情符号, 请删除后再保存");
        setAsrIsUploading(false);
        const targetNode = asrRef.current.querySelector(`#asr-${emojiIndex}`);
        if (targetNode) {
          targetNode.scrollIntoView({ behavior: "smooth" });
          targetNode.style.backgroundColor = "gold";
          setTimeout(() => {
            targetNode.style.backgroundColor = "transparent";
          }, 600);
        }
        return;
      }
      uploadContentToOssFile(url, listOmitGolden)
        .then(() => {
          message.success("字幕编辑成功");
          reloadAsr();
        })
        .catch(() => {
          message.error("字幕编辑失败");
        })
        .finally(() => {
          setAsrIsUploading(false);
        });
    },
    [url, reloadAsr]
  );

  const trimmedSearchKeyword = R.pipe(R.defaultTo(""), R.trim)(searchKeyword);
  const [selectAsrValue, setSelectAsrValue] = useState(undefined);

  const dataSource = useMemo(() => {
    if (trimmedSearchKeyword === "") {
      return [];
    }
    return asrList.filter((item) => {
      return item.text.includes(trimmedSearchKeyword);
    });
  }, [asrList, trimmedSearchKeyword]);

  useEffect(() => {
    if (!target.current) return;

    const handleTimeUpdate = () => {
      setCurrentTime(target.current.currentTime);
    };

    target.current.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      target.current.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [target]);

  const options = useMemo(() => {
    const firstChunk = R.slice(0, 5, dataSource);
    return firstChunk.map((item) => {
      return <Select.Option value={item.value}>{item.text}</Select.Option>;
    });
  }, [dataSource]);

  const onChangeOrSelect = (value)=>{
    setSelectAsrValue(value);
    const targetNode = asrRef.current.querySelector(`#asr-${value}`);
    if (targetNode) {
      targetNode.scrollIntoView({ behavior: "smooth" });
      targetNode.style.backgroundColor = "gold";
      setTimeout(() => {
        targetNode.style.backgroundColor = "transparent";
      }, 600);
    }
  }

  return (
    <div
      style={{
        height: 700,
        display: "flex",
        flexDirection: "column",
      }}
      ref={asrRef}
    >
      <div className="button-group flex mb10" style={{ marginBottom: 12 }}>
        <Select
          style={{ width: "100%", zIndex: 99999 }}
          getPopupContainer={() => asrRef.current}
          value={selectAsrValue}
          showSearch
          showArrow={false}
          placeholder="搜索字幕"
          filterOption={false}
          defaultActiveFirstOption
          onSearch={(value)=>{
            console.log('search', value);
            debounceSetSearchKeyword(value);
          }}
          onChange={onChangeOrSelect}
          onSelect={onChangeOrSelect}
          notFoundContent={null}
          autoClearSearchValue={false}
          allowClear
        >
          {options}
        </Select>

        <Button
          typeof="primary"
          disabled={originAsrIsSameWithAsr}
          className="ml-2"
          type="primary"
          onClick={() => {
            updateAsr(asrList);
          }}
        >
          保存字幕
        </Button>

        <SelectOneTemplate
          orderId={R.pathOr("", ["orderId"], props)}
          onCreate={(params) => {
            return createVideoByTimeline(
              mediaId,
              params.template_id,
              params.name,
              selectedIndexList.map((index) => asrList[index]),
              userId,
              params
            );
          }}
        >
          {({ open }) => {
            return (
              <Button
                className="ml-auto ml-2"
                disabled={selectedIndexList.length === 0}
                type={selectedIndexList.length === 0 ? "default" : "primary"}
                onClick={() => {
                  if (selectedIndexList.length === 0) {
                    return;
                  }
                  if (!originAsrIsSameWithAsr) {
                    message.warn("请点击保存字幕");
                    return;
                  }
                  open()
                    .then(() => {
                      message.success("生成任务发起成功");
                    })
                    .catch((e) => {
                      if (!e) {
                        return;
                      }
                      message.error("生成任务发起失败");
                    });
                }}
              >
                生成字幕视频片段
              </Button>
            );
          }}
        </SelectOneTemplate>
      </div>
      <div
        className="ant-card-bordered p-2 asr-container"
        style={{
          flex: 1,
          overflow: "auto",
          position: "relative",
        }}
      >
        <FloatToItsParentRight right={30} top={480}>
          <Button
            type="primary"
            style={{ zIndex: 1 }}
            onClick={() => {
              const targetNode = asrRef.current.querySelector(`.asr-container`);
              if (targetNode) {
                //  animate scroll to top
                targetNode.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }}
            shape="circle"
            icon="arrow-up"
            size="large"
          />
        </FloatToItsParentRight>

        <FloatToItsParentRight right={-10} top={550}>
          <Button
            style={{ zIndex: 1 }}
            type="primary"
            onClick={() => {
              const targetNode = asrRef.current.querySelector(`.asr-container`);
              if (targetNode) {
                //  animate scroll to bottom
                targetNode.scrollTo({
                  top: targetNode.scrollHeight,
                  behavior: "smooth",
                });
              }
            }}
            shape="circle"
            icon="arrow-down"
            size="large"
          />
        </FloatToItsParentRight>

        <div
          style={{
            display: goldSlogan.length > 0 ? "block" : "none",
          }}
        >
          <h3>系统推荐金句</h3>
          {goldSlogan.map((item, index) => {
            const currentGoldSloganIsNotChanged = asrList.some(
              (asrItem) =>
                asrItem.text === item.text &&
                asrItem.startTime === item.startTime &&
                asrItem.endTime === item.endTime
            );

            if (index > 2 && !goldSloganIsExpanded) {
              return null;
            }
            return (
              <div
                onClick={() => {
                  if (!currentGoldSloganIsNotChanged) {
                    message.info("字幕已修改, 无法定位到系统推荐金句");
                    return;
                  }
                  //  scroll to the current  indexed element
                  const cIndex = asrList.findIndex(
                    (asrItem) =>
                      asrItem.text === item.text &&
                      asrItem.startTime === item.startTime &&
                      asrItem.endTime === item.endTime
                  );
                  if(!asrRef.current) {
                    return;
                  }
                  const target = asrRef.current.querySelector(`#asr-${cIndex}`);
                  if (target) {
                    target.scrollIntoView({ behavior: "smooth" });
                    target.style.backgroundColor = "gold";
                    setTimeout(() => {
                      target.style.backgroundColor = "transparent";
                    }, 600);
                  }
                }}
                key={index}
                style={{
                  backgroundColor: currentGoldSloganIsNotChanged
                    ? "#FFF777"
                    : "lightgray",
                  padding: "4px 8px",
                  marginBottom: 8,
                }}
              >
                {item.text}
              </div>
            );
          })}
          {goldSlogan.length > 3 && (
            <div
              style={{
                cursor: "pointer",
                color: "#1890ff",
                textAlign: "center",
              }}
              onClick={() => {
                setGoldSloganIsExpanded(!goldSloganIsExpanded);
              }}
            >
              <Button type="link">
                {goldSloganIsExpanded ? "收起" : "展开"}
              </Button>
            </div>
          )}
          <Divider />
        </div>

        <List
          loading={asrIsUploading}
          itemLayout="horizontal"
          dataSource={asrList}
          renderItem={(item, index) => {
            const currentIsActive =
              currentTime > item.startTime && currentTime < item.endTime;
            return (
              <List.Item
                id={`asr-${index}`}
                style={{
                  backgroundColor:
                    currentTime >= item.startTime && currentTime < item.endTime
                      ? "#e6f7ff"
                      : "transparent",
                  transition: "background-color 0.3s",
                }}
              >
                <List.Item.Meta
                  avatar={
                    <input
                      type="checkbox"
                      checked={selectedIndexList.includes(index)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const originSavedIndexList = R.clone(selectedIndexList);
                        let newSelectedIndexList;
                        if (checked) {
                          newSelectedIndexList = R.uniq([
                            ...originSavedIndexList,
                            index,
                          ]);
                        } else {
                          newSelectedIndexList = R.without(
                            [index],
                            originSavedIndexList
                          );
                        }
                        const sortedSelectedIndexList = R.sort(
                          (a, b) => a - b,
                          newSelectedIndexList
                        );
                        setSelectedIndexList(sortedSelectedIndexList);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  title={
                    <div className="flex flex-y-center">
                      {formatSecondsToHumanReadable(item.startTime)}
                      <div style={{ margin: "0 10px" }}>----</div>
                      {formatSecondsToHumanReadable(item.endTime)}
                      {currentIsActive ? (
                        <Icon type="sound" className="ml-2" theme="twoTone" />
                      ) : (
                        <Icon
                          onClick={() => {
                            if (target.current) {
                              target.current.currentTime = item.startTime;
                              target.current.play();
                            }
                          }}
                          type="play-circle"
                          className="ml-2 cursor-pointer"
                          theme="twoTone"
                        />
                      )}
                    </div>
                  }
                  description={
                    <HoverEditableInput
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      style={{
                        color: item.golden ? "#FA8C16" : "black",
                        backgroundColor: item.golden
                          ? "#FFF7E6"
                          : "transparent",
                      }}
                      value={item.text}
                      onChange={(e) => {
                        //  如果编辑的被选中了, 那么应该去掉选中的这一项
                        setAsrList((prevAsrList) => {
                          const newAsrList = R.clone(prevAsrList);
                          newAsrList[index].text = e.target.value;
                          return newAsrList;
                        });
                      }}
                    />
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/widgets/asr-line.js